import React, { ReactNode } from 'react';
import { SpellUsageBossOption, SpellUsageFightFilter } from '../../types';
import { AggregatedHealer } from '../types';
import ConfirmDialog from '../../components/ConfirmDialog';
import HelpTip from '../../components/HelpTip';
import { heavyLogWarningText } from '../heavyLogGate';
import { useHeavyLogGate } from '../useHeavyLogGate';
import TbcLoadedSummary from './TbcLoadedSummary';
import TbcPhaseHeader from './TbcPhaseHeader';
import TriStateCheckbox from './TriStateCheckbox';
import HeavyLogWarning from './HeavyLogWarning';
import WclQueryProgress from './WclQueryProgress';
import { WclProgressSnapshot } from '../useWclProgress';

interface TbcSelectionPhaseProps {
    loadedLogUrls: string[];
    players: AggregatedHealer[];
    bosses: SpellUsageBossOption[];
    selectedPlayerName?: string;
    selectedPlayerNames?: string[];
    fightFilter: SpellUsageFightFilter;
    selectedEncounterIds: number[];
    minDurationSeconds: string;
    loading: boolean;
    analyzeButtonLabel: string;
    showMinDurationFilter?: boolean;
    minDurationHelpText?: string;
    extraFilters?: ReactNode;
    hidePlayerPicker?: boolean;
    hideFightFilter?: boolean;
    title?: string;
    titleHelp?: ReactNode;
    playerHelp?: ReactNode;
    playerSectionTitle?: string;
    playerPlaceholder?: string;
    bossesHelp?: ReactNode;
    bossDescription?: ReactNode;
    playerCountLabel?: string;
    onSelectedPlayerChange?: (name: string) => void;
    onTogglePlayerName?: (name: string) => void;
    onSelectAllPlayers?: () => void;
    onClearPlayers?: () => void;
    onFightFilterChange?: (filter: SpellUsageFightFilter) => void;
    onToggleEncounterId: (encounterId: number) => void;
    onToggleAllEncounterIds: () => void;
    onToggleRaidEncounterIds: (encounterIds: number[]) => void;
    onMinDurationChange: (value: string) => void;
    onAnalyze: () => void;
    onBack: () => void;
    wclSnapshot?: WclProgressSnapshot | null;
}

const PRESET_FILTERS: Array<{ value: SpellUsageFightFilter; label: string; hint: string }> = [
    { value: 'kills', label: 'All kills', hint: '' },
    { value: 'encounters', label: 'All encounters', hint: ' — incl. wipes' },
    { value: 'everything', label: 'Everything', hint: ' — incl. trash' },
];

function groupBossesByRaid(bosses: SpellUsageBossOption[]): Array<{ label: string; bosses: SpellUsageBossOption[] }> {
    const groups: Array<{ label: string; bosses: SpellUsageBossOption[] }> = [];
    for (const boss of bosses) {
        const label = boss.raidLabel || 'Other';
        const last = groups[groups.length - 1];
        if (last && last.label === label) {
            last.bosses.push(boss);
            continue;
        }
        groups.push({ label, bosses: [boss] });
    }
    return groups;
}

const TbcSelectionPhase: React.FC<TbcSelectionPhaseProps> = ({
    loadedLogUrls,
    players,
    bosses,
    selectedPlayerName = '',
    selectedPlayerNames,
    fightFilter,
    selectedEncounterIds,
    minDurationSeconds,
    loading,
    analyzeButtonLabel,
    showMinDurationFilter = false,
    minDurationHelpText,
    extraFilters,
    hidePlayerPicker = false,
    hideFightFilter = false,
    title,
    titleHelp,
    playerHelp,
    playerSectionTitle,
    playerPlaceholder,
    bossesHelp,
    bossDescription,
    playerCountLabel,
    onSelectedPlayerChange,
    onTogglePlayerName,
    onSelectAllPlayers,
    onClearPlayers,
    onFightFilterChange,
    onToggleEncounterId,
    onToggleAllEncounterIds,
    onToggleRaidEncounterIds,
    onMinDurationChange,
    onAnalyze,
    onBack,
    wclSnapshot,
}) => {
    const multiSelect = selectedPlayerNames !== undefined;
    const hasHealer = hidePlayerPicker
        ? players.length > 0
        : multiSelect
          ? selectedPlayerNames.length > 0
          : selectedPlayerName.length > 0;
    const allPlayersSelected = multiSelect && players.length > 0 && selectedPlayerNames.length === players.length;
    const headerTitle = title ?? (multiSelect ? 'Select Healers and Fight Filter' : 'Select Healer and Fight Filter');
    const selectedBossSet = new Set(selectedEncounterIds);
    const allBossesSelected = bosses.length > 0 && bosses.every((boss) => selectedBossSet.has(boss.encounterId));
    const someBossesSelected = selectedEncounterIds.length > 0;
    const hasBossSelection = bosses.length === 0 || someBossesSelected;
    const logCount = loadedLogUrls.length;
    const gate = useHeavyLogGate(logCount, onAnalyze);

    return (
        <div className="selection-phase">
            <TbcPhaseHeader title={headerTitle} backLabel="← Back to URLs" onBack={onBack} help={titleHelp} />

            <TbcLoadedSummary
                logCount={loadedLogUrls.length}
                healerCount={players.length}
                playerCountLabel={playerCountLabel}
            />

            {!hidePlayerPicker && (
                <div className="druid-selection">
                    <h4 className="heading-with-help">
                        {playerSectionTitle ?? (multiSelect ? 'Select Healers' : 'Select Healer')}
                        {playerHelp}
                    </h4>
                    {multiSelect && (
                        <p className="analyzer-description">
                            Pick one healer for a full report, or several to compare.
                        </p>
                    )}
                    {multiSelect ? (
                        <>
                            <div className="tbc-filter-row">
                                <h5 style={{ margin: 0 }}>{selectedPlayerNames.length} selected</h5>
                                <div className="tbc-filter-row-actions">
                                    <button
                                        type="button"
                                        className="tbc-filter-clear"
                                        onClick={onSelectAllPlayers}
                                        disabled={players.length === 0 || allPlayersSelected}
                                    >
                                        Select all
                                    </button>
                                    <button
                                        type="button"
                                        className="tbc-filter-clear"
                                        onClick={onClearPlayers}
                                        disabled={selectedPlayerNames.length === 0}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            <div className="talent-toggles">
                                {players.map((player) => {
                                    const checked = selectedPlayerNames.includes(player.name);
                                    return (
                                        <label key={player.name} className="talent-toggle">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => {
                                                    if (onTogglePlayerName) {
                                                        onTogglePlayerName(player.name);
                                                    }
                                                }}
                                            />
                                            <span className="talent-name">
                                                {player.name} ({player.class}) — {player.appearances.length}/
                                                {loadedLogUrls.length} log
                                                {loadedLogUrls.length === 1 ? '' : 's'}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <div className="druid-dropdown">
                            <select
                                value={selectedPlayerName}
                                onChange={(e) => {
                                    if (onSelectedPlayerChange) {
                                        onSelectedPlayerChange(e.target.value);
                                    }
                                }}
                                className="druid-select"
                            >
                                <option value="">{playerPlaceholder ?? 'Select a healer...'}</option>
                                {players.map((player) => (
                                    <option key={player.name} value={player.name}>
                                        {player.name} ({player.class}) — {player.appearances.length}/
                                        {loadedLogUrls.length} logs
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            )}

            <div className="talent-selection" style={{ marginTop: '20px' }}>
                {!hideFightFilter && (
                    <>
                        <h4 className="heading-with-help">
                            Fight Filter
                            <HelpTip label="Fight filter">
                                <ul>
                                    <li>
                                        <strong>All kills</strong> — bosses you killed
                                    </li>
                                    <li>
                                        <strong>All encounters</strong> — boss pulls, including wipes
                                    </li>
                                    <li>
                                        <strong>Everything</strong> — bosses and trash
                                    </li>
                                </ul>
                            </HelpTip>
                        </h4>
                        <div className="talent-toggles">
                            {PRESET_FILTERS.map(({ value, label, hint }) => (
                                <label key={value} className="talent-toggle">
                                    <input
                                        type="radio"
                                        name="tbcFightFilter"
                                        checked={fightFilter === value}
                                        onChange={() => {
                                            if (onFightFilterChange) {
                                                onFightFilterChange(value);
                                            }
                                        }}
                                    />
                                    <span className="talent-name">
                                        {label}
                                        {hint}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </>
                )}

                {bosses.length > 0 && (
                    <>
                        <div className="tbc-filter-row">
                            <h5 className="heading-with-help" style={{ margin: 0 }}>
                                Bosses
                                <HelpTip label="How boss checkboxes work" wide>
                                    {bossesHelp ?? (
                                        <>
                                            <p>All bosses start selected. Uncheck any you want to leave out.</p>
                                            {loadedLogUrls.length > 1 && (
                                                <p>If the same pull is in more than one log, only one copy is kept.</p>
                                            )}
                                        </>
                                    )}
                                </HelpTip>
                            </h5>
                        </div>
                        {bossDescription ?? (
                            <p className="analyzer-description">
                                All bosses start selected. Uncheck any you want to leave out.
                                {loadedLogUrls.length > 1 ? ' Duplicate pulls across logs are kept only once.' : ''}
                            </p>
                        )}
                        <div className="talent-toggles">
                            <TriStateCheckbox
                                className="talent-toggle tbc-boss-select-all"
                                checked={allBossesSelected}
                                indeterminate={someBossesSelected && !allBossesSelected}
                                onChange={onToggleAllEncounterIds}
                            >
                                All bosses
                            </TriStateCheckbox>
                            {groupBossesByRaid(bosses).map((group) => {
                                const raidIds = group.bosses.map((boss) => boss.encounterId);
                                const raidSelectedCount = raidIds.filter((id) => selectedBossSet.has(id)).length;
                                const raidAllSelected = raidSelectedCount === raidIds.length && raidIds.length > 0;
                                return (
                                    <div key={group.label} className="tbc-boss-raid-group">
                                        <TriStateCheckbox
                                            className="talent-toggle tbc-boss-raid-heading"
                                            labelClassName="tbc-boss-raid-label"
                                            checked={raidAllSelected}
                                            indeterminate={raidSelectedCount > 0 && !raidAllSelected}
                                            onChange={() => onToggleRaidEncounterIds(raidIds)}
                                        >
                                            {group.label}
                                        </TriStateCheckbox>
                                        {group.bosses.map((boss) => {
                                            const checked = selectedBossSet.has(boss.encounterId);
                                            return (
                                                <label key={boss.encounterId} className="talent-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => onToggleEncounterId(boss.encounterId)}
                                                    />
                                                    <span className="talent-name">
                                                        {boss.name} — {boss.pullCount} pull
                                                        {boss.pullCount === 1 ? '' : 's'} ({boss.killCount} kill
                                                        {boss.killCount === 1 ? '' : 's'}) in {boss.reportCount}/
                                                        {loadedLogUrls.length} log
                                                        {loadedLogUrls.length === 1 ? '' : 's'}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {showMinDurationFilter && (
                    <div className="tbc-duration-filter">
                        <span>Minimum fight duration (seconds)</span>
                        <input
                            type="number"
                            min={0}
                            step={1}
                            placeholder="0 = no minimum"
                            value={minDurationSeconds}
                            onChange={(e) => onMinDurationChange(e.target.value)}
                        />
                        {minDurationHelpText && <span className="analyzer-description">{minDurationHelpText}</span>}
                    </div>
                )}

                {extraFilters}
            </div>

            <HeavyLogWarning logCount={logCount} />
            <button
                type="button"
                onClick={gate.requestContinue}
                disabled={loading || !hasHealer || !hasBossSelection}
                className="process-button"
            >
                {loading ? 'Analyzing...' : analyzeButtonLabel}
            </button>
            {loading && <WclQueryProgress snapshot={wclSnapshot ?? null} />}
            <ConfirmDialog
                open={gate.confirmOpen}
                title={`Analyze ${logCount} reports?`}
                message={heavyLogWarningText(logCount)}
                confirmLabel="Analyze anyway"
                onConfirm={gate.confirm}
                onCancel={gate.cancel}
            />
        </div>
    );
};

export default TbcSelectionPhase;
